import { useEffect, useRef } from 'react';
import { recentService } from '../services/recentService';
import { useRecentStore } from '../store/recentStore';

/**
 * Record that the user opened this record, once, when it RESOLVES (M4 · 86d44z223).
 *
 * ⚠️ **On resolve, NOT on mount — this is the whole trap.** Detail pages fetch in a mount effect,
 * so at mount the record is still `null`: a mount-keyed capture stores an empty label (exactly the
 * failure Recent exists to avoid) and, on a page that keeps its previous record while refetching,
 * can store the PREVIOUS one under the new id. Callers therefore pass values read off the resolved
 * record and this hook no-ops until both `entityId` and `label` are non-empty.
 *
 * ⚠️ Sales has no per-record ROUTE at all — the workspaces carry selection in `?id=`. "The selected
 * record resolved" is the one trigger that works for both shapes, which is the other reason this is
 * not keyed to navigation.
 *
 * `moduleId` is NOT passed: the server derives it from the kind, so a client cannot place a row in
 * a module it does not belong to.
 */
export function useRecordVisit(
  entityType: string,
  entityId: string | number | null | undefined,
  label: string | null | undefined
): void {
  // One write per (type, id) per mount. Without this, any re-render that changes an unrelated
  // dependency would restamp the row — harmless for correctness (it is an upsert) but a request
  // per keystroke on pages with live search.
  const lastKey = useRef<string | null>(null);

  useEffect(() => {
    const id = entityId === null || entityId === undefined ? '' : String(entityId);
    const text = label?.trim() ?? '';
    // Not resolved yet. Silence is correct here — a partial row is worse than no row.
    if (id.length === 0 || text.length === 0) return;

    const key = `${entityType}|${id}`;
    if (lastKey.current === key) return;
    lastKey.current = key;

    // Fire and forget: never awaited, never surfaces an error, never blocks the page — but its
    // ANSWER is used. The server returns the row it wrote (module included, which only it can
    // decide), and that goes straight into the panel's cache. Before this, the Recent list did not
    // move until the panel was reopened or the page reloaded, because the one event that changes it
    // is this very call and nothing was listening to it.
    //
    // ⛔ Not polling and not persisted: the list only changes as a result of what this client just
    // did, and a `localStorage` copy would leak one user's record history to the next person at a
    // shared terminal — the reason M4 went server-side in the first place.
    void recentService.recordVisit({ entityType, entityId: id, label: text }).then((row) => {
      if (row) useRecentStore.getState().recordVisited(row);
    });
  }, [entityType, entityId, label]);
}
